import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  getAccountCommissions,
  updateBulkCommissionsAccount,
} from "../../store/slices/commissionAndRepaymentSlice";
import {
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Square,
  CheckCircle,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatDateTime } from "../../utils/formatDateUtils";
import {
  CommissionPDFData,
  generateCommissionPDF,
} from "../../pages/EPCCustomerManager/generateCommissionPDF";
import { ICommissionGroup } from "../../models/commissionAndRepayment.model";
import ApproveCommissionModal from "./ApproveCommissionModal";

const ACTIONABLE_STATUSES = ["pending", "rejected"];
interface AccountCommissionViewProps {
  status: string;
}

const AccountCommissionView: React.FC<AccountCommissionViewProps> = ({
  status,
}) => {
  const dispatch: RootDispatch = useDispatch();

  const allApprovedCommissions = useSelector(
    (state: RootState) => state.commissionAndRepayment.allApprovedCommissions,
  );
  const loading = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.loadingState
        .fetchAllApprovedCommissionsLoading,
  );
  const updatingBulk = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.loadingState.updatingBulkCommissionLoading,
  );

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedGroups, setSelectedGroups] = useState<Record<string, boolean>>(
    {},
  );
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [showApproveModal, setShowApproveModal] = useState(false);

  useEffect(() => {
    dispatch(getAccountCommissions({ page, limit, status }));
  }, [dispatch, page, limit, status]);

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const toggleGroupSelection = useCallback((groupKey: string) => {
    setSelectedGroups((prev) => {
      const updated = { ...prev };
      if (updated[groupKey]) {
        delete updated[groupKey];
      } else {
        updated[groupKey] = true;
      }
      return updated;
    });
  }, []);

  const getSelectedCommissionHistoryIds = useCallback(() => {
    const selectedIds: string[] = [];
    const groups = allApprovedCommissions?.groups || [];

    groups.forEach((group) => {
      const groupKey = group._id || `group-${groups.indexOf(group)}`;

      if (selectedGroups[groupKey]) {
        // Get all pending commission history IDs from this selected group
        group.commissionHistory.forEach((item) => {
          if (
            item.accountStatus &&
            ACTIONABLE_STATUSES.includes(item.accountStatus) &&
            item._id
          ) {
            selectedIds.push(item._id);
          }
        });
      }
    });

    return selectedIds;
  }, [allApprovedCommissions, selectedGroups]);
  useEffect(() => {
    setSelectedGroups({});
  }, [status]);
  const handleBulkAction = useCallback(
    async (action: "approve" | "reject", proofFile?: File | null) => {
      const selectedIds = getSelectedCommissionHistoryIds();

      if (selectedIds.length === 0) {
        toast.error("Please select at least one commission batch");
        return;
      }

      try {
        await dispatch(
          updateBulkCommissionsAccount({
            commissionHistoryIds: selectedIds,
            action,
            proofFile,
          }),
        ).unwrap();

        dispatch(getAccountCommissions({ page, limit, status }));

        setSelectedGroups({});
      } catch (error: any) {
        console.error("Failed to update commissions:", error);
        toast.error(error.message || "Failed to update commissions");
      }
    },
    [dispatch, getSelectedCommissionHistoryIds, page, limit, status],
  );

  const calculateTotals = useCallback(() => {
    let totalSelectedGroups = 0;
    let totalSelectedCommissions = 0;
    let totalSelectedAmount = 0;
    let totalPendingAmount = 0;
    let totalPendingCount = 0;

    const groups = allApprovedCommissions?.groups || [];

    groups.forEach((group) => {
      const groupKey = group._id || `group-${groups.indexOf(group)}`;
      const isGroupSelected = !!selectedGroups[groupKey];

      group.commissionHistory.forEach((item) => {
        const amount = item.calculatedCommission || 0;

        // Count pending items
        if (item.accountStatus === "pending") {
          totalPendingAmount += amount;
          totalPendingCount++;
        }

        // Count selected items
        if (
          isGroupSelected &&
          (item.accountStatus === "pending" ||
            item.accountStatus === "rejected")
        ) {
          totalSelectedCommissions++;
          totalSelectedAmount += amount;
        }
      });

      if (isGroupSelected) {
        totalSelectedGroups++;
      }
    });

    return {
      totalSelectedGroups,
      totalSelectedCommissions,
      totalSelectedAmount,
      totalPendingAmount,
      totalPendingCount,
    };
  }, [allApprovedCommissions, selectedGroups]);

  const {
    totalSelectedGroups,
    totalSelectedCommissions,
    totalSelectedAmount,
    totalPendingCount,
  } = calculateTotals();

  const generateGroupPDF = (group: ICommissionGroup) => {
    if (!group?.commissionHistory?.length) return;

    const pdfData: CommissionPDFData[] = group.commissionHistory.map(
      (item: any) => ({
        commissionId: item._id,
        commissionAmount: item.commissionAmount,
        calculatedCommission: item.calculatedCommission,
        status: item.status,
        plantSize: item.quoteId?.plantSize,
        customerName: item.customerId?.customerName,
        confirmAt: item.quoteId?.confirmAt,
        excessAmount: item.quoteId?.excessAmount,
        commissionValue: group.salesRepresentative?.commissionValue,
        adjustmentAmount: item.quoteId?.adjustmentAmount,
        customCommission: item.quoteId?.customCommission,
        materialDeduction: item.materialDeduction,
      }),
    );

    const approvedBy = group.commissionHistory[0]?.cseId?.username;
    const salesRepName = group.salesRepresentative?.name;
    const panNumber = group.salesRepresentative?.panNumber;
    const bankDetails = {
      bankAccountName: group.salesRepresentative?.bankAccountName,
      bankAccountNumber: group.salesRepresentative?.bankAccountNumber,
      ifsc: group.salesRepresentative?.ifsc,
    };

    generateCommissionPDF(
      pdfData,
      approvedBy,
      salesRepName,
      panNumber,
      bankDetails,
    );
  };

  const handleOpenProof = (proof?: string) => {
    if (!proof) return;
    window.open(proof, "_blank", "noopener,noreferrer");
  };

  const calculateBatchTotal = (historyItems: any[]) => {
    return historyItems.reduce((sum, item) => {
      return (
        sum +
        (item.calculatedCommission || item.commissionAmount || 0) -
        (item.materialDeduction || 0) +
        (item.adjustAmount || 0)
      );
    }, 0);
  };

  const getStatusBadge = (accountStatus: string) => {
    const baseClasses =
      "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium";

    if (accountStatus === "approved") {
      return (
        <span
          className={`${baseClasses} bg-green-100 text-green-800 border border-green-200`}
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          Account Approved
        </span>
      );
    } else if (accountStatus === "rejected") {
      return (
        <span
          className={`${baseClasses} bg-red-100 text-red-800 border border-red-200`}
        >
          <XCircle className="w-3 h-3 mr-1" />
          Account Rejected
        </span>
      );
    } else {
      return (
        <span
          className={`${baseClasses} bg-yellow-100 text-yellow-800 border border-yellow-200`}
        >
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Pending Account Approval
        </span>
      );
    }
  };

  const pagination = allApprovedCommissions?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const selectedCommissions = useMemo(() => {
    const result: {
      commissionId: string;
      customerName?: string;
      calculatedCommission: number;
    }[] = [];

    const groups = allApprovedCommissions?.groups || [];

    groups.forEach((group) => {
      const groupKey = group._id || `group-${groups.indexOf(group)}`;

      if (selectedGroups[groupKey]) {
        group.commissionHistory.forEach((item) => {
          if (
            item.accountStatus &&
            ACTIONABLE_STATUSES.includes(item.accountStatus)
          ) {
            result.push({
              commissionId: item._id || "",
              customerName: item.customerId?.customerName,
              calculatedCommission:
                item.calculatedCommission ?? item.commissionAmount ?? 0,
            });
          }
        });
      }
    });

    return result;
  }, [allApprovedCommissions, selectedGroups]);

  // const visibleGroups = useMemo(() => {
  //   const groups = allApprovedCommissions?.groups || [];

  //   return groups.filter((group) =>
  //     group.commissionHistory.some((item) => item.accountStatus !== "approved"),
  //   );
  // }, [allApprovedCommissions]);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4">
      {/* Selection Summary */}
      {totalSelectedGroups > 0 && (
        <div className="mb-4">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                <span className="font-medium">
                  {totalSelectedGroups} batch
                  {totalSelectedGroups > 1 ? "es" : ""} selected
                </span>
                <span className="ml-3 text-gray-600">
                  ({totalSelectedCommissions} commission
                  {totalSelectedCommissions > 1 ? "s" : ""})
                </span>
                {totalSelectedAmount > 0 && (
                  <span className="ml-3 text-green-600">
                    ₹{totalSelectedAmount.toLocaleString("en-IN")}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowApproveModal(true)}
                  disabled={updatingBulk}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve Selected
                </button>

                <button
                  onClick={() => handleBulkAction("reject")}
                  disabled={updatingBulk}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Reject Selected
                </button>

                <button
                  onClick={() => setSelectedGroups({})}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading commission data...</span>
        </div>
      )}

      {!loading && (
        <>
          {(allApprovedCommissions?.groups || []).length > 0 && (
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(allApprovedCommissions?.groups || []).length} batch
                {(allApprovedCommissions?.groups || []).length > 1 ? "es" : ""}
                <span className="ml-3 text-gray-600">
                  ({totalPendingCount} pending commission
                  {totalPendingCount > 1 ? "s" : ""})
                </span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {allApprovedCommissions?.groups.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  No Commission Records Found
                </h3>
              </div>
            ) : (
              (allApprovedCommissions?.groups || [])?.map((group, index) => {
                const groupKey = group._id || `group-${index}`;
                const isExpanded = expandedGroups.has(groupKey);
                const isGroupSelected = !!selectedGroups[groupKey];
                const batchTotal = calculateBatchTotal(group.commissionHistory);
                const approvedBy =
                  group.commissionHistory[0]?.cseId?.username || "Unknown";
                const salesRep = group.salesRepresentative;

                const hasPendingItems = group.commissionHistory.some(
                  (item) =>
                    item.accountStatus === "pending" ||
                    item.accountStatus === "rejected",
                );

                return (
                  <div
                    key={groupKey}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Group Header */}
                    <div
                      className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100"
                      onClick={() => toggleGroup(groupKey)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {hasPendingItems && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleGroupSelection(groupKey);
                              }}
                              className="p-1 rounded hover:bg-gray-100"
                              title="Select entire batch"
                            >
                              {isGroupSelected ? (
                                <CheckSquare className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-300 hover:text-gray-500" />
                              )}
                            </button>
                          )}

                          {/* Expand/Collapse Button */}
                          <div className="flex items-center gap-3 hover:bg-gray-50 p-1 rounded">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <div className="font-medium text-gray-900">
                                  {salesRep?.name} -
                                </div>
                                <div className="font-medium text-gray-900">
                                  {formatDateTime(group.cseApprovedAt || "")}
                                </div>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Approved by: {approvedBy}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-base font-bold text-blue-600">
                              ₹{batchTotal.toLocaleString("en-IN")}
                            </div>
                            <div className="text-xs text-gray-500">
                              Batch Total
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              generateGroupPDF(group);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-md bg-sky-600 hover:bg-sky-700 text-white transition-all"
                          >
                            <FileText className="w-3 h-3" />
                            PDF
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 px-5 py-4 border-t border-gray-100 bg-slate-50">
                        {group.commissionHistory.map((h: any, idx: number) => {
                          const historyId = h._id;
                          if (!historyId) return null;

                          const customerName =
                            h.customerId?.customerName || "-";
                          const amount =
                            h.calculatedCommission -
                              (h.materialDeduction || 0) +
                              (h.adjustAmount || 0) ||
                            h.commissionAmount ||
                            0;

                          return (
                            <div
                              key={historyId || idx}
                              className="flex items-center justify-between rounded-md p-3 border border-slate-100 bg-white shadow-sm"
                            >
                              {/* LEFT SIDE */}
                              <div className="text-sm">
                                {/* Customer */}
                                <div className="font-medium text-slate-800">
                                  {customerName}
                                  <span className="text-slate-600">
                                    {" "}
                                    ({h.quoteId?.plantSize} kW)
                                  </span>
                                </div>

                                {/* ===== NORMAL COMMISSION ===== */}
                                {!h.quoteId?.customCommissionType &&
                                  !h.quoteId?.excessAmount && (
                                    <div className="flex justify- gap-8 text-xs text-slate-400 mt-1">
                                      <span>
                                        Commission (
                                        {
                                          group.salesRepresentative
                                            ?.commissionType
                                        }
                                        )
                                      </span>
                                      <span>₹ {h.commissionAmount}</span>
                                    </div>
                                  )}

                                {/* ===== CUSTOM COMMISSION ===== */}
                                {h.quoteId?.customCommissionType && (
                                  <div className="space-y-1 text-xs text-slate-400 mt-1">
                                    <div className="flex justify-between gap-14">
                                      <span>
                                        Commission (
                                        {
                                          group.salesRepresentative
                                            ?.commissionType
                                        }
                                        )
                                      </span>
                                      <span className="font-medium text-gray-700">
                                        ₹{" "}
                                        {
                                          group.salesRepresentative
                                            ?.commissionValue
                                        }
                                      </span>
                                    </div>

                                    <div className="flex justify-between gap-14">
                                      <span>
                                        Total
                                        {group.salesRepresentative
                                          ?.commissionType === "per_kw" && (
                                          <>
                                            {" "}
                                            (
                                            {
                                              group.salesRepresentative
                                                ?.commissionValue
                                            }{" "}
                                            × {h.quoteId?.plantSize} kW)
                                          </>
                                        )}
                                      </span>
                                      <span className="font-medium text-slate-700">
                                        ₹ {h.commissionAmount}
                                      </span>
                                    </div>

                                    <div className="flex justify-between">
                                      <span>Deduction</span>
                                      <span className="font-medium text-red-600">
                                        − ₹ {h.quoteId.customCommission}
                                      </span>
                                    </div>
                                    {h.materialDeduction !== undefined &&
                                      h.materialDeduction > 0 && (
                                        <div className="flex justify-between">
                                          <span>Deduction</span>
                                          <span className="font-medium text-red-600">
                                            − ₹ {h.materialDeduction || 0}
                                          </span>
                                        </div>
                                      )}
                                  </div>
                                )}

                                {/* ===== EXCESS AMOUNT ===== */}
                                {h.quoteId?.excessAmount && (
                                  <div className="space-y-1 text-xs text-slate-400 mt-1">
                                    <div className="flex justify-between">
                                      <span>
                                        Total
                                        {group.salesRepresentative
                                          ?.commissionType === "per_kw" && (
                                          <>
                                            {" "}
                                            (
                                            {
                                              group.salesRepresentative
                                                ?.commissionValue
                                            }{" "}
                                            × {h.quoteId?.plantSize} kW)
                                          </>
                                        )}
                                      </span>
                                      <span className="font-medium text-slate-700">
                                        ₹ {h.commissionAmount}
                                      </span>
                                    </div>

                                    <div className="flex justify-between">
                                      <span>Excess Amount</span>
                                      <span className="font-medium text-green-600">
                                        + ₹ {h.quoteId.excessAmount}
                                      </span>
                                    </div>

                                    <p className="text-xs text-red-600">
                                      Amount shown is post 8.9% GST deduction
                                    </p>
                                  </div>
                                )}
                                {h.quoteId?.adjustmentAmount && (
                                  <div className="text-xs text-red-600 flex item-center justify-between mt-1">
                                    <span>Adjustment Amount:</span>
                                    <span className="font-medium">
                                      ₹ {h.quoteId?.adjustmentAmount}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* RIGHT SIDE */}
                              <div className="text-sm text-right">
                                <div className="flex items-center justify-end gap-2 font-semibold text-lg">
                                  {group.salesRepresentative?.commissionType ===
                                    "per_kw" &&
                                    !h.quoteId?.customCommissionType &&
                                    !h.quoteId?.excessAmount && (
                                      <span className="text-xs text-gray-400">
                                        ({h.commissionAmount} ×{" "}
                                        {h.quoteId.plantSize} kW)
                                      </span>
                                    )}

                                  <span>₹{amount.toLocaleString("en-IN")}</span>
                                </div>

                                <div className="space-y-1.5 flex flex-col items-end mt-2">
                                  {h.accountStatus &&
                                    getStatusBadge(h.accountStatus)}

                                  {h.proof && (
                                    <button
                                      onClick={() => handleOpenProof(h.proof)}
                                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                    >
                                      <Download className="w-3 h-3" />
                                      View Proof
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {pagination.pages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-700">
                Page {pagination.page} of {pagination.pages} • Total{" "}
                {pagination.total} commission batches
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    pagination.hasPrevPage
                      ? "border-gray-300 text-gray-700 hover:bg-gray-50"
                      : "border-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    pagination.hasNextPage
                      ? "border-gray-300 text-gray-700 hover:bg-gray-50"
                      : "border-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ApproveCommissionModal
        open={showApproveModal}
        commissions={selectedCommissions}
        loading={updatingBulk}
        onClose={() => setShowApproveModal(false)}
        onConfirm={(file) => {
          setShowApproveModal(false);
          handleBulkAction("approve", file);
        }}
      />
    </div>
  );
};

export default AccountCommissionView;
